/**
 * The thin client for `/api/email/send_rfp_email`, the route every staff-composed outbound email
 * goes through (it owns the test-event chokepoint, attachment validation, sanitization and
 * logging).
 *
 * Extracted from `@/components/ui/utils` so a surface that needs to send an email does not have
 * to import that module's whole graph - Slack notifications, payments, draft actions and the
 * admin database client - to get twenty lines of `fetch`. `components/ui/utils` re-exports this,
 * so its existing callers are unchanged.
 */

/** One attachment on an outbound email, base64-encoded by the caller. */
export interface EmailAttachment {
  content: string;
  filename: string;
  type: string;
  disposition?: string;
  content_id?: string;
}

/** The send route's request body. */
export interface TableEmailPayload {
  event_id: number;
  to: string;
  from: string;
  from_name?: string;
  subject: string;
  htmlContent: string;
  attachments?: EmailAttachment[];
  cc?: string | string[];
  bcc?: string;
  /**
   * Staff confirmed they want to send despite the event being flagged as a test event. Bypasses
   * the route's test-event chokepoint. Ignored for non-test events.
   */
  allowTestEvent?: boolean;
}

/** Absolute on the server (no relative base), relative in the browser (no CORS preflight). */
function getApiUrl(path: string): string {
  if (typeof window === "undefined") {
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
    return `${baseUrl}${path}`;
  }
  return path;
}

/**
 * Sends one email through the RFP send route.
 *
 * @param payload - The envelope and body; see {@link TableEmailPayload}.
 * @returns The route's raw response, for callers that read its body.
 * @throws When the route refuses, with the route's own reason (e.g. "Cannot send RFP emails for
 *   test events") rather than a generic failure, so the caller's toast can say what happened.
 */
export const sendTableEmail = async (payload: TableEmailPayload): Promise<Response> => {
  const response = await fetch(getApiUrl("/api/email/send_rfp_email"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Surface the route's actual reason so the caller's toast isn't a generic "Failed to send
    // email".
    let message = "Failed to send email";
    try {
      const body = await response.json();
      if (body?.error || body?.message) message = body.error || body.message;
    } catch {
      // Non-JSON response - keep the generic message.
    }
    throw new Error(message);
  }

  return response;
};
